import React, { useEffect, useState } from 'react';
import { Edit, FileDown, PencilLine, Plus, Trash, Upload } from 'lucide-react';
import { Link, useHistory } from 'react-router-dom';
import CustomDatePicker from '../../../components/DatePickerVi';
import { CourseGetPaging } from '../../../services/courseService';
import Pagination from '../../../components/Pagination';
import LoadingOverlay from '../../../components/Static/LoadingOverlay';
import { Course } from '../../../models/model';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { fetchLicenseClasses } from '../../../store/licenseSlice';
import { formatDate } from '../../../lib/utils';
import { toast } from 'react-hot-toast';
import { StudentDownloadTemplate } from '../../../services/studentService';
import { ExamResultImportListCourseExam, ExportListCourseExam } from '../../../services/examResultService';
import moment from 'moment-timezone';

export default function EndSemester() {
	const router = useHistory();
	const dispatch: AppDispatch = useDispatch();
	const licenseClasses = useSelector(
		(state: RootState) => state.licenseClasses.items,
	);
	const [isLoading, setIsLoading] = useState(true);
	const [courses, setCourses] = useState<Course[]>([]);
	const [searchText, setSearchText] = useState<string>('');
	const [searchLicenseClass, setSearchLicenseClass] = useState<string>('');
	const [startDate, setStartDate] = useState<Date | null>();
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [pagination, setPagination] = useState({
		pageIndex: 1,
		pageSize: 50,
		totalRecords: 0,
		totalPages: 1,
	});
	const [selectedCourse, setSelectedCourse] = useState<any[]>([]);
	const handleSearch = () => {
		// Reset to first page when searching
		fetchCourses(1);
	};
	const fetchCourses = async (
		pageIndex: number,
		keepPageIndex: boolean = false,
	) => {
		setIsLoading(true);
		try {
			const res = await CourseGetPaging({
				pageIndex: keepPageIndex ? pagination.pageIndex : pageIndex,
				pageSize: pagination.pageSize,
				freeTextSearch: searchText,
				licenseClassId: searchLicenseClass || null,
				startDate: startDate ? startDate.toISOString() : null,
				endDate: endDate ? endDate.toISOString() : null,
			});
			setCourses(res.result.items);
			setPagination((prev) => ({
				...prev,
				pageIndex: keepPageIndex ? prev.pageIndex : pageIndex,
				totalRecords: res.result.totalRecord,
				totalPages: Math.ceil(
					res.result.totalRecord / pagination.pageSize,
				),
			}));
		} catch (error) {
			console.error('Failed to fetch courses:', error);
		} finally {
			setIsLoading(false);
		}
	};
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};
	const handlePageChange = (newPageIndex: number) => {
		if (newPageIndex < 1 || newPageIndex > pagination.totalPages) return;
		fetchCourses(newPageIndex);
	};

	const handleDateChange = (type: 'start' | 'end') => (date: Date | null) => {
		if (type === 'start') {
			setStartDate(date);
			// If new start date > current end date, reset end date
			if (date && endDate && date > endDate) {
				setEndDate(null);
			}
		} else {
			setEndDate(date);
		}
		// Reset to first page when date changes
		fetchCourses(1);
	};
	useEffect(() => {
		if (licenseClasses.length === 0) {
			dispatch(fetchLicenseClasses());
		}
		fetchCourses(1);
	}, [searchLicenseClass]);

	async function exportStudentList() {
		if (selectedCourse.length === 0) {
			toast.error(`Vui lòng chọn khóa học để xuất danh sách thi!`);
		} else {
			try {
				const blob = await ExportListCourseExam(selectedCourse);

				const downloadUrl = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = downloadUrl;
				link.setAttribute('download', 'Danh_sach_thi'+moment(new Date()).format('DD/MM/YYYY')+ '.xlsx');
				document.body.appendChild(link);
				link.click();

				// Clean up
				link.parentNode?.removeChild(link);
				window.URL.revokeObjectURL(downloadUrl);
				toast.success('Tải xuống thành công');
			} catch (error: any) {
				const errorMsg = error?.responseException?.exceptionMessage
					|| error?.message
					|| 'Lỗi';
				toast.error(errorMsg);
			}
		}
	}
	const handleExamResultImport = async (file: File) => {
		try {
			setIsLoading(true);
			const res = await ExamResultImportListCourseExam(file);
			toast.success(res.message);

		} catch (error: any) {
			const errorMsg = error?.responseException?.exceptionMessage
				|| error?.message
				|| "Lỗi";
			toast.error(errorMsg);
		} finally {
			setIsLoading(false);
		}
	};
	return (
		<div className="bg-white rounded-lg shadow-md p-6">
			<div className="mx-auto px-4 py-8">
				<h1 className="text-3xl font-bold text-gray-800 mb-5">
					<div className={'flex items-center'}>Cuối kì</div>
				</h1>
				<div className="md:flex md:justify-between md:items-center mb-6 mt-6 w-100">
					<div className="mb-6 w-100">
						<div className="md:flex items-end gap-3">
							<div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3 ">
								<div className="space-x-1">
									<label className="text-xs text-gray-500 block">
										Từ ngày
									</label>
									<CustomDatePicker
										// @ts-ignore
										selected={startDate}
										onChange={handleDateChange('start')}
										placeholder="Từ"
										id="startDate"
										name="startDate"
									/>
								</div>
								<div className="space-x-1">
									<label className="text-xs text-gray-500 block">
										Đến ngày
									</label>
									<CustomDatePicker
										// @ts-ignore
										selected={endDate}
										onChange={handleDateChange('end')}
										placeholder="Đến"
										id="endDate"
										name="endDate"
										className="h-[38px]"
									/>
								</div>
								<div className="space-x-1">
									<label className="text-xs text-gray-500 block">
										Hạng
									</label>
									<select
										className="md:min-w-max w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
										value={searchLicenseClass}
										onChange={(e) =>
											setSearchLicenseClass(
												e.target.value,
											)
										}>
										<option value="">Tất cả</option>
										{licenseClasses.map((lc) => (
											<option key={lc.id} value={lc.id}>
												{lc.name}
											</option>
										))}
									</select>
								</div>
								<div className="space-x-1">
									<label className="text-xs text-gray-500 block">
										Tìm kiếm
									</label>
									<div className="flex gap-2">
										<input
											type="text"
											placeholder="Tìm khóa..."
											value={searchText}
											onChange={(e) =>
												setSearchText(e.target.value)
											}
											onKeyDown={handleKeyDown}
											className="w-100 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
										/>
										<button
											onClick={handleSearch}
											className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 ">
											Tìm
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className={'flex justify-content-end gap-x-2'}>
					<button
						className="flex items-center px-4 py-2 mb-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
						onClick={() => exportStudentList()}>
						<FileDown className="mr-2" />
						Xuất danh sách thi
					</button>
					<button
						className="flex items-center px-4 py-2 mb-2 bg-green-600 text-white rounded-md hover:bg-green-700"
						onClick={() => document.getElementById('examResultImport')?.click()}
					>
						<Upload className="mr-2" />
						Cập nhật kết quả thi
					</button>
					<input
						id="examResultImport"
						type="file"
						accept=".xlsx,.xls,.xlsm"
						onChange={async (e) => {
							const file = e.target.files?.[0];
							if (file) {
								const result = await handleExamResultImport(file);
								e.target.value = '';
							}
						}}
						className="hidden"
					/>

				</div>
			</div>
			<div className="w-full overflow-x-auto">
				<table className="min-w-max w-full divide-y divide-gray-200">
					<thead className="bg-gray-50">
					<tr>
						<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
							<div
								className={'flex flex-column items-center'}>
								Tất cả
								<input
									type="checkbox"
									id={`check-all`}
									onChange={(e) => {
										if (e.target.checked) {
											setSelectedCourse(courses);
										} else {
											setSelectedCourse([]);
										}
									}}
									className="mr-2 w-4 h-4 mt-2"
								/>
							</div>
						</th>
						<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-2">
							STT
						</th>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Tên khóa học
						</th>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Hạng
						</th>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Số HV
						</th>
						<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							Thời gian
						</th>
					</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
					{courses.map((course, index) => (
						<tr
							key={course.id}
							className={
								'hover:bg-gray-50 transition-colors'
							}>
							<td
								className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500  cursor-pointer"
								onClick={() => console.log(course)}>
								<input
									type="checkbox"
									id={`course-${course.id}`}
									checked={selectedCourse.includes(
										course.id,
									)}
									onChange={(e) => {
										if (e.target.checked) {
											setSelectedCourse([
												...selectedCourse,
												course.id,
											]);
										} else {
											setSelectedCourse(
												selectedCourse.filter(
													(id) => id !== course.id,
												),
											);
										}
									}}
									className="mr-2 w-5 h-5"
								/>
							</td>
							<td
								className="px-4 py-3 whitespace-nowrap text-center text-sm text-gray-500  cursor-pointer"
								onClick={() =>
									router.push(
										`/courses/schedule/${course.id}`,
									)
								}>
								{(pagination.pageIndex - 1) *
									pagination.pageSize +
									index +
									1}
							</td>
							<td
								className="px-4 py-3 whitespace-nowrap cursor-pointer"
								onClick={() =>
									router.push(
										`/courses/schedule/${course.id}`,
									)
								}>
								<div
									className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-sm md:text-base">
									{course.name}
								</div>
								<div className="text-xs md:text-sm text-gray-500">
									{course.classCode}
								</div>
							</td>
							<td
								className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 cursor-pointer"
								onClick={() =>
									router.push(
										`/courses/schedule/${course.id}`,
									)
								}>
								{course.licenseClassName}
							</td>
							<td
								className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 cursor-pointer"
								onClick={() =>
									router.push(
										`/courses/schedule/${course.id}`,
									)
								}>
								{course.totalStudents || 0}
							</td>
							<td
								className="px-4 py-3 whitespace-nowrap cursor-pointer"
								onClick={() =>
									router.push(
										`/courses/schedule/${course.id}`,
									)
								}>
								<div className="flex flex-col space-y-1.5 text-xs md:text-sm text-gray-700">
									{course.openingDate && (
										<div className="flex items-center">
												<span className="w-20 md:w-24 font-medium text-gray-500">
													Khai giảng:
												</span>
											<span className="font-medium">
													{formatDate(
														course.openingDate,
													)}
												</span>
										</div>
									)}

									{course.registrationDate && (
										<div className="flex items-center">
												<span className="w-20 md:w-24 font-medium text-gray-500">
													Đăng ký SH:
												</span>
											<span className="font-medium">
													{formatDate(
														course.registrationDate,
													)}
												</span>
										</div>
									)}

									{course.closingDate && (
										<div className="flex items-center">
												<span className="w-20 md:w-24 font-medium text-gray-500">
													Bế giảng:
												</span>
											<span className="font-medium">
													{formatDate(
														course.closingDate,
													)}
												</span>
										</div>
									)}

									<div className="flex items-center">
											<span className="w-20 md:w-24 font-medium text-gray-500">
												Thời gian:
											</span>
										<span className="font-medium">
												{course.startDate
													? formatDate(
														course.startDate,
													)
													: 'Chưa xác định'}
											<span className="mx-1 text-gray-400">
													→
												</span>
											{course.endDate
												? formatDate(course.endDate)
												: 'Chưa xác định'}
											</span>
									</div>
								</div>
							</td>
						</tr>
					))}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			<Pagination
				pagination={pagination}
				isLoading={isLoading}
				onPageChange={handlePageChange}
				label="khóa học"
			/>

			{isLoading && <LoadingOverlay message="Đang tải dữ liệu..." />}

			{!isLoading && courses.length === 0 && (
				<div className="mt-4 text-center text-gray-500">
					Không tìm thấy khóa học nào
				</div>
			)}
		</div>
	);
}
